<template>
  <f7-page>
    <f7-navbar :title="$t('tontine.payoutTitle')" back-link />
    <AppLayout>
      <template #main>
    <f7-block v-if="loading" strong inset>
      <f7-preloader /> {{ $t('tontine.loading') }}
    </f7-block>

    <template v-else-if="payout">
      <!-- Payout card -->
      <f7-block strong inset>
        <div class="payout-head">
          <div>
            <h2>{{ money(payout.amount) }}</h2>
            <p>{{ $t('tontine.to') }} : {{ payout.beneficiaryDisplayName || $t('tontine.beneficiary') }}</p>
          </div>
          <f7-badge :class="statusClass">{{ statusLabel }}</f7-badge>
        </div>
        <f7-list media-list no-hairlines>
          <f7-list-item :title="$t('tontine.currency')" :after="payout.currency" />
          <f7-list-item :title="$t('tontine.approvalsProgress')" :after="`${payout.approvalsGranted || 0} / ${payout.approvalsRequired || 0}`" />
          <f7-list-item v-if="payout.providerPayoutReference" :title="$t('tontine.providerReference')" :after="payout.providerPayoutReference" />
          <f7-list-item :title="$t('tontine.preparedAt')" :after="formatDate(payout.preparedAt)" />
          <f7-list-item v-if="payout.submittedAt" :title="$t('tontine.submittedAt')" :after="formatDate(payout.submittedAt)" />
          <f7-list-item v-if="payout.executedAt" :title="$t('tontine.executedAt')" :after="formatDate(payout.executedAt)" />
          <f7-list-item v-if="payout.failureReason" :title="$t('tontine.failureReason')" :after="payout.failureReason" />
        </f7-list>
      </f7-block>

      <!-- Approval progress -->
      <f7-block strong inset>
        <f7-progressbar :progress="approvalProgress" color="green" />
        <p class="progress-text">{{ approvalText }}</p>

        <f7-list media-list v-if="payout.approvals && payout.approvals.length">
          <f7-list-item
            v-for="approval in payout.approvals"
            :key="approval.id"
            :title="shortId(approval.approverUserId)"
            :after="approval.decision === 'APPROVED' ? $t('tontine.decision.APPROVED') : $t('tontine.decision.REJECTED')"
            :subtitle="`${approval.comment || ''}${approval.comment ? ' · ' : ''}${formatDate(approval.decidedAt)}`"
          />
        </f7-list>
        <p v-else class="text-color-gray">{{ $t('tontine.noApprovalsYet') }}</p>
      </f7-block>

      <!-- Approver actions -->
      <f7-block v-if="canDecide" strong inset>
        <div class="approve-actions">
          <f7-button fill raised round color="green" class="action-flex" @click="approve">{{ $t('tontine.approve') }}</f7-button>
          <f7-button outline raised round color="red" class="action-flex" @click="reject">{{ $t('tontine.reject') }}</f7-button>
        </div>
        <p class="hint">{{ $t('tontine.dualControlHint') }}</p>
      </f7-block>

      <!-- Mode + regulatory explainer -->
      <f7-block strong inset class="info-notice">
        <p class="text-color-gray">
          <strong>{{ $t('tontine.payoutModeTitle') }}</strong><br>
          {{ payoutModeBody }}
        </p>
      </f7-block>
    </template>
     </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import { useI18n } from 'vue-i18n'
import { useTontineStore } from '@/js/pinia-stores/tontine'
import { useKeycloakStore } from '@/js/pinia-stores/keycloak'
import AppLayout from "../../components/common/AppLayout.vue";


const { t } = useI18n()
const props = defineProps({ campaignId: String, groupId: String, payoutId: String })
const store = useTontineStore()
const keycloak = useKeycloakStore()

const plan = computed(() => store.plan)
const cycleDetail = computed(() => store.cycleDetail)
const loading = computed(() => store.loading)
const subject = computed(() => keycloak.subject)

const payout = computed(() => {
  const fromCycle = cycleDetail.value?.payout
  if (fromCycle?.id === props.payoutId) return fromCycle
  return (store.payouts || []).find((p) => p.id === props.payoutId)
})

const statusMeta = computed(() => {
  const statusMap = {
    NOT_READY: { label: t('tontine.payoutStatus.NOT_READY'), cls: 'badge-gray' },
    DRAFT: { label: t('tontine.payoutStatus.DRAFT'), cls: 'badge-orange' },
    AWAITING_APPROVAL: { label: t('tontine.payoutStatus.AWAITING_APPROVAL'), cls: 'badge-orange' },
    APPROVED: { label: t('tontine.payoutStatus.APPROVED'), cls: 'badge-green' },
    SUBMITTED_TO_PARTNER: { label: t('tontine.payoutStatus.SUBMITTED_TO_PARTNER'), cls: 'badge-green' },
    EXECUTED: { label: t('tontine.payoutStatus.EXECUTED'), cls: 'badge-green' },
    FAILED: { label: t('tontine.payoutStatus.FAILED'), cls: 'badge-red' },
    CANCELLED: { label: t('tontine.payoutStatus.CANCELLED'), cls: 'badge-red' },
  }
  return statusMap[payout.value?.status] || { label: payout.value?.status || '—', cls: 'badge-gray' }
})
const statusLabel = computed(() => statusMeta.value.label)
const statusClass = computed(() => statusMeta.value.cls)

const approvalProgress = computed(() => {
  const required = payout.value?.approvalsRequired || 1
  const granted = payout.value?.approvalsGranted || 0
  return Math.min(100, Math.round((granted / required) * 100))
})
const approvalText = computed(
  () => t('tontine.approvalsProgress') + ' : ' + (payout.value?.approvalsGranted || 0) + ' / ' + (payout.value?.approvalsRequired || 0)
)

const alreadyDecided = computed(() =>
  (payout.value?.approvals || []).some((a) => a.approverUserId === subject.value)
)
const canDecide = computed(
  () =>
    plan.value?.canApprove &&
    payout.value?.status === 'AWAITING_APPROVAL' &&
    !alreadyDecided.value
)

const payoutModeBody = computed(() => {
  const mode = plan.value?.operatingMode
  if (mode === 'COORDINATION_ONLY') return t('tontine.payoutMode.coordination')
  if (mode === 'REGULATED_PARTNER_SAVINGS') return t('tontine.payoutMode.regulated')
  return t('tontine.payoutMode.orchestrated')
})

onMounted(load)

async function load() {
  try {
    await Promise.all([
      store.loadSummary(props.campaignId),
      store.fetchPayouts(props.campaignId),
    ])
  } catch (err) {
    f7.dialog.alert(err.response?.data?.message || err.message || t('tontine.loadError'))
  }
}

function approve() {
  f7.dialog.confirm(t('tontine.approveConfirm'), t('tontine.approve'), () => decide('APPROVED', ''))
}
function reject() {
  f7.dialog.prompt(t('tontine.rejectPrompt'), t('tontine.reject'), (comment) => decide('REJECTED', comment || ''), '')
}

async function decide(decision, comment) {
  f7.preloader.show()
  try {
    await store.decidePayout(props.campaignId, props.payoutId, { decision, comment: comment || undefined })
    f7.toast.show({ text: decision === 'APPROVED' ? t('tontine.approved') : t('tontine.rejected'), position: 'bottom', closeTimeout: 2000 })
    await load()
  } catch (err) {
    f7.dialog.alert(err.response?.data?.message || err.message || t('tontine.decideError'))
  } finally {
    f7.preloader.hide()
  }
}

function shortId(id) {
  if (!id) return t('tontine.member')
  const parts = String(id).split('-')
  return parts.length ? parts.slice(0, 4).join('-') : id
}

function money(v, c) {
  return new Intl.NumberFormat(keycloak.locale || 'fr', {
    style: 'currency',
    currency: c || payout.value?.currency || 'XOF',
    maximumFractionDigits: 0,
  }).format(v || 0)
}
function formatDate(v) {
  return v
    ? new Intl.DateTimeFormat(keycloak.locale || 'fr', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(
        new Date(v)
      )
    : '—'
}
</script>

<style scoped>
.payout-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.payout-head h2 { margin: 0; font-size: 30px; }
.payout-head p { margin: 4px 0 0; color: var(--f7-on-surface-variant); font-size: 14px; }
.badge-green { background: #00b894; }
.badge-orange { background: #f0932b; }
.badge-red { background: #ee5253; }
.badge-gray { background: #9e9e9e; }
.progress-text { font-size: 13px; color: var(--f7-on-surface-variant); margin-top: 8px; }
.approve-actions { display: flex; gap: 10px; }
.action-flex { flex: 1; }
.hint { font-size: 12px; color: var(--f7-on-surface-variant); margin-top: 10px; }
.info-notice p { font-size: 13px; line-height: 1.5; margin: 0; }
</style>